import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, user } = useAuth();
  const { branding } = useBranding();

  const getRedirectPath = (userProfile) => {
    const from = location.state?.from?.pathname;
    if (from && from !== '/login') return from;
    switch (userProfile?.role) {
      case 'admin': return '/admin';
      case 'driver': return '/driver';
      default: return '/app';
    }
  };

  useEffect(() => {
    if (user && profile) {
      navigate(getRedirectPath(profile), { replace: true });
    }
  }, [user, profile]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (localLoading) return;
    setLocalLoading(true);
    setError('');

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (authError) throw authError;
      if (!data.user) throw new Error('No user returned from sign in.');

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, role, full_name, phone')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Profile query error:', profileError);
        throw new Error('Could not load your profile. Check RLS policies.');
      }
      
      let finalProfile = profileData;

      // Self-healing: If the profile is missing because the DB trigger failed during sign up,
      // create it now since the user is authenticated and allowed to insert their own profile.
      if (!finalProfile) {
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .upsert(
            { 
              id: data.user.id, 
              full_name: data.user.user_metadata?.full_name || 'User',
              phone: data.user.user_metadata?.phone || null,
              role: 'customer' 
            },
            { onConflict: 'id' }
          )
          .select('id, role, full_name, phone')
          .single();

        if (createError) {
          console.error('Profile healing error:', createError);
          throw new Error('Profile not found and could not be automatically created.');
        }
        finalProfile = newProfile;
      }

      const target = getRedirectPath(finalProfile);
      navigate(target, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      const msg = err.message === 'Invalid login credentials' ? 'Invalid email or password.' : err.message;
      setError(msg);
      setLocalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-light flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <div className="h-16 w-16 bg-brand-navy rounded-2xl flex items-center justify-center shadow-premium transform hover:rotate-12 transition-transform overflow-hidden">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <SafeIcon name={branding.logo_icon || 'Droplet'} className="text-brand-gold text-3xl" />
            )}
          </div>
        </div>
        <h2 className="text-center text-3xl font-extrabold text-brand-navy mb-2">
          {branding.app_name}
        </h2>
        <p className="text-center text-gray-500 mb-8 text-xs uppercase tracking-[0.2em] font-medium">
          {branding.seo_description || 'Professional Garment Care'}
        </p>

        <div className="bg-white py-10 px-8 shadow-card rounded-[2.5rem] border border-gray-100">
          <form className="space-y-5" onSubmit={handleLogin}>
            <Input
              label="Email Address"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@mercury.com"
              disabled={localLoading}
            />
            <div className="space-y-1">
              <Input
                label="Password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={localLoading}
              />
              <div className="flex justify-end">
                <button type="button" className="text-xs font-semibold text-brand-gold hover:text-brand-navy transition-colors">
                  Forgot Password?
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-2xl text-sm border border-red-100 flex items-center gap-3">
                <SafeIcon name="AlertCircle" className="shrink-0 text-lg" />
                <span className="font-medium">{error}</span>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              isLoading={localLoading}
              className="w-full h-14 text-lg shadow-premium hover:shadow-xl"
            >
              {localLoading ? 'Signing In...' : 'Secure Sign In'}
            </Button>
          </form>

          <div className="mt-10 pt-8 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              New to {branding.app_name.split(' ')[0]}?{' '}
              <button
                onClick={() => navigate('/register')}
                className="font-bold text-brand-navy hover:text-brand-gold transition-colors underline decoration-brand-gold/30 underline-offset-4"
                type="button"
              >
                Create an account
              </button>
            </p>
          </div>
        </div>

        <p className="mt-8 text-center text-gray-400 text-xs">
          Secure encrypted connection enabled
        </p>
      </div>
    </div>
  );
}